import { cdnPath } from "@/utility/api";
import { Building, Award, User, ExternalLink } from "lucide-react";

const WebinarHero = ({ details, WebinarForm }) => {
  return (
    <section className="relative w-full min-h-screen flex items-center justify-center bg-black overflow-hidden">
      <div className="absolute inset-0 bg-slate-950">
        {/* Grid Pattern */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.4) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.4) 1px, transparent 1px)
            `,
            backgroundSize: "80px 80px",
          }}
        />
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-transparent to-slate-950/80" />
      </div>
      {/* Background Gradients */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/10 via-black to-black opacity-90" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(128,128,128,0.1),transparent_50%)]" />

      {/* Content Container */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-16 lg:py-24 h-full min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch w-full min-h-[80vh]">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left space-y-6 lg:space-y-8 flex flex-col justify-center">
            {/* Main Headline */}
            <h1 className="text-3xl md:text-3xl lg:text-[34px] font-bold leading-tight">
              <div className="flex">
                <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent">
                  Break into Founder's Office Roles
                </span>
              </div>
              <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent"></span>

              <div className="flex items-center gap-3 justify-center lg:justify-start mt-2">
                <div className="h-0.5 w-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full" />
                <span className="text-indigo-400 font-bold text-lg tracking-wider">
                  OFF-CAMPUS
                </span>
                <div className="h-0.5 w-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" />
              </div>
            </h1>

            {/* Power Statement Card */}
            <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 rounded-xl p-4 border border-gray-700/50 backdrop-blur-xl hover:border-indigo-500/30 transition-all duration-300">
              <p className="text-lg font-bold text-white leading-relaxed">
                No fancy IIT tag. No uncle at a VC firm.
              </p>
              <p className="text-lg text-gray-300 mt-2">
                Just a{" "}
                <span className="text-indigo-400 font-semibold">
                  solid strategy
                </span>{" "}
                to crack elite startup roles — even as a fresher.
              </p>
            </div>

            {/* Enhanced Speaker Showcase */}
            <div className="relative">
              {/* Subtle background glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl blur opacity-50" />

              <div className="relative bg-gradient-to-br from-gray-900/95 to-black/95 rounded-2xl p-6 border border-indigo-500/30 backdrop-blur-xl">
                {/* Clean Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-indigo-400" />
                    <span className="text-indigo-400 font-semibold text-sm uppercase tracking-wide">
                      Featuring
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-2 py-1 bg-green-500/20 rounded-full border border-green-500/30">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-green-400 text-xs font-semibold">
                      LIVE
                    </span>
                  </div>
                </div>

                {/* Speaker Profile - Mobile: Vertical, Desktop: Horizontal */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                  {/* Profile Image - MUCH BIGGER */}
                  <div className="relative flex-shrink-0">
                    <div className="w-40 h-40 sm:w-48 sm:h-48 md:w-36 md:h-36 lg:w-40 lg:h-40 rounded-2xl overflow-hidden border-2 border-indigo-400/30 shadow-2xl">
                      <img
                        src={`${cdnPath}/Yuganksha.png`}
                        alt="YUGANKSHA BHASIN"
                        className="w-full h-full object-cover"
                        style={{
                          imageRendering: "crisp-edges",
                          msInterpolationMode: "bicubic",
                        }}
                      />
                    </div>
                    {/* Status dot - bigger too */}
                    <div className="absolute -bottom-3 -right-3 w-8 h-8 bg-green-500 rounded-full border-4 border-black flex items-center justify-center shadow-lg">
                      <div className="w-3 h-3 bg-white rounded-full" />
                    </div>
                  </div>

                  {/* Bio Content - Centered on Mobile, Left-aligned on Desktop */}
                  <div className="flex-1 text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                      Yuganksha Bhasin
                    </h3>

                    {/* Enhanced Company Experience with Logo */}
                    <div className="flex items-center justify-center sm:justify-start gap-3 mb-3 md:mt-4 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg p-2 border border-emerald-500/20">
                      <Building className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <div className="flex items-center gap-2">
                        <span className="text-emerald-400 text-sm sm:text-base md:text-[15px] font-medium">
                          Ex-Founder's Office
                        </span>
                        {/* The Souled Store Logo */}
                        <div className="flex items-center gap-1 bg-white/95 rounded px-2 py-1 shadow-sm">
                          <img
                            src={`${cdnPath}/souledstorelogo.jpeg`}
                            alt="The Souled Store"
                            className="w-5 h-5 object-contain"
                            onError={(e) => {
                              // Fallback if logo doesn't load
                              e.target.style.display = "none";
                              e.target.nextElementSibling.style.display =
                                "inline";
                            }}
                          />
                          <span
                            className="text-gray-800 text-xs font-bold hidden"
                            style={{ display: "none" }}
                          >
                            TSS
                          </span>
                          <span className="text-gray-800 text-xs font-semibold">
                            The Souled Store
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start justify-center sm:justify-start gap-2 mb-4">
                      <Award className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300 text-sm sm:text-base leading-relaxed">
                        Ex-ZS Associates
                      </span>
                    </div>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="mt-6 pt-4 border-t border-gray-700/50">
                  <div className="flex items-center justify-between">
                    <span className="text-purple-300 text-sm font-medium">
                      🎯 Expert
                    </span>
                    <a
                      href="https://www.linkedin.com/in/yuganksha-bhasin/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-indigo-400 text-sm font-semibold hover:text-indigo-300 transition-colors"
                    >
                      <span>Know about her</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="w-full h-full flex justify-center">
            <WebinarForm
              price={details?.price}
              canBook={details?.canBook}
              seats_left={details?.seats_left}
              date={details?.date}
            />
          </div>
        </div>
      </div>

      {/* Bottom Gradient Overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black to-transparent" />
    </section>
  );
};

export default WebinarHero;
